import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { ChevronDown, ChevronUp, Plus, Trash2, X } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import { refLabel, scorableRefs } from "@/lib/ref-scoring";
import type {
  WeeklyPointSummaryConfig,
  WeeklyPointSummaryRow,
  WidgetConfig,
} from "@/lib/schema";
import type { WidgetSettingsEditorProps } from "@/widgets/types";

type Props = WidgetSettingsEditorProps<WeeklyPointSummaryConfig> & {
  allWidgets?: WidgetConfig[];
};

// The drop target for «Ikke lagt til». Row ids are UUIDs, so it cannot
// collide with one.
const UNPLACED = "unplaced";

// What a chip is being dragged out of: a row id, or UNPLACED.
interface ChipSource {
  from: string;
  ref: string;
}

export function SettingsEditor({
  config,
  onChange,
  errors = {},
  allWidgets = [],
}: Props) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor),
  );

  const update = (patch: Partial<WeeklyPointSummaryConfig>) => onChange(patch);
  const fieldError = (key: string) =>
    errors[key] ? (
      <p className="field-error" data-testid={`widget-error-${key}`}>
        {errors[key]}
      </p>
    ) : null;

  const addRow = () => {
    const next: WeeklyPointSummaryRow = {
      id: crypto.randomUUID(),
      name: "",
      widgetIds: [],
    };
    update({ rows: [...config.rows, next] });
  };

  const updateRow = (id: string, patch: Partial<WeeklyPointSummaryRow>) => {
    update({
      rows: config.rows.map((r) => (r.id === id ? { ...r, ...patch } : r)),
    });
  };

  const removeRow = (id: string) => {
    update({ rows: config.rows.filter((r) => r.id !== id) });
  };

  const moveRow = (id: string, delta: -1 | 1) => {
    const idx = config.rows.findIndex((r) => r.id === id);
    if (idx < 0) return;
    const newIdx = idx + delta;
    if (newIdx < 0 || newIdx >= config.rows.length) return;
    const next = [...config.rows];
    const [moved] = next.splice(idx, 1);
    next.splice(newIdx, 0, moved);
    update({ rows: next });
  };

  const removeWidgetFromRow = (rowId: string, widgetId: string) => {
    const row = config.rows.find((r) => r.id === rowId);
    if (!row) return;
    updateRow(rowId, {
      widgetIds: row.widgetIds.filter((id) => id !== widgetId),
    });
  };

  // A reference sits on at most one row, so a drop moves it: off the row it
  // came from, and appended to the target unless it is the box. Only the
  // source row loses it, so a duplicate left over from before the rule stays
  // on its other row until the user drags that one too.
  const handleDragEnd = (e: DragEndEvent) => {
    if (!e.over) return;
    const source = e.active.data.current as ChipSource | undefined;
    if (!source) return;
    const target = String(e.over.id);
    if (target === source.from) return;
    update({
      rows: config.rows.map((r) => {
        if (r.id === source.from) {
          return {
            ...r,
            widgetIds: r.widgetIds.filter((id) => id !== source.ref),
          };
        }
        if (r.id === target && !r.widgetIds.includes(source.ref)) {
          return { ...r, widgetIds: [...r.widgetIds, source.ref] };
        }
        return r;
      }),
    });
  };

  // Same rule WeekView scores by, plus "can actually score": a pure
  // tracking meal-log (maxScoreWeek 0) is never offered. A kind that declares
  // scorable parts is offered AS its parts and never as a whole, so no row
  // can count a half and its container twice; a row that wants the total
  // lists both parts (docs/adr/0010). Only the parts this config has: a
  // workout-log without planning is offered whole. A disabled instance earns
  // nothing, so it has no points to miss and is never unplaced.
  const candidateRefs = allWidgets.flatMap(scorableRefs);
  const placed = new Set(config.rows.flatMap((r) => r.widgetIds));
  const unplaced = candidateRefs.filter((ref) => !placed.has(ref));

  const labelOf = (ref: string): string =>
    refLabel(ref, allWidgets) ?? "(slettet widget)";

  return (
    <div
      className="widget-settings"
      data-testid={`widget-settings-${config.id}`}
    >
      <div className="setting-row">
        <label className="setting-label" htmlFor={`label-${config.id}`}>
          Tittel
        </label>
        <div className="setting-control">
          <input
            id={`label-${config.id}`}
            className="input"
            type="text"
            value={config.label}
            onChange={(e) => update({ label: e.target.value })}
            data-testid="widget-label-input"
          />
          {fieldError("label")}
        </div>
      </div>

      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <DropZone
          id={UNPLACED}
          className="unplaced-refs"
          data-testid="unplaced-refs"
        >
          <div className="section-head">
            <h3 data-testid="unplaced-refs-title">
              Ikke lagt til ({unplaced.length})
            </h3>
            <span className="hint">Dra til en rad for å telle poengene.</span>
          </div>
          {unplaced.length === 0 ? (
            <p className="hint" data-testid="unplaced-refs-empty">
              Alle poeng er lagt til.
            </p>
          ) : (
            <div className="day-picker">
              {unplaced.map((ref) => (
                <Chip
                  key={ref}
                  from={UNPLACED}
                  widgetRef={ref}
                  label={labelOf(ref)}
                  data-testid={`unplaced-ref-chip-${ref}`}
                />
              ))}
            </div>
          )}
        </DropZone>

        <div className="task-list">
          <div className="section-head">
            <h3>Rader</h3>
            <span className="hint">
              Hver rad summerer poeng fra én eller flere dag-widgets.
            </span>
          </div>
          {fieldError("rows")}
          {fieldError("rowNames")}
          {fieldError("rowWidgets")}
          {fieldError("rowDuplicates")}
          {config.rows.map((row, idx) => (
            <DropZone
              key={row.id}
              id={row.id}
              className="weekly-point-summary-row"
              data-testid={`weekly-point-summary-row-${row.id}`}
            >
              <div className="row-handle">
                <button
                  type="button"
                  className="row-handle-btn"
                  onClick={() => moveRow(row.id, -1)}
                  disabled={idx === 0}
                  data-testid={`row-move-up-${row.id}`}
                  aria-label="Flytt opp"
                >
                  <ChevronUp size={14} />
                </button>
                <button
                  type="button"
                  className="row-handle-btn"
                  onClick={() => moveRow(row.id, 1)}
                  disabled={idx === config.rows.length - 1}
                  data-testid={`row-move-down-${row.id}`}
                  aria-label="Flytt ned"
                >
                  <ChevronDown size={14} />
                </button>
              </div>
              <div className="row-body">
                <input
                  type="text"
                  className="input"
                  placeholder="Oppgave"
                  value={row.name}
                  onChange={(e) => updateRow(row.id, { name: e.target.value })}
                  data-testid={`row-name-input-${row.id}`}
                />
                <div className="day-picker">
                  {row.widgetIds.length === 0 ? (
                    <span
                      className="hint"
                      data-testid={`row-empty-hint-${row.id}`}
                    >
                      Dra en widget hit.
                    </span>
                  ) : (
                    row.widgetIds.map((wid) => (
                      <Chip
                        key={wid}
                        from={row.id}
                        widgetRef={wid}
                        label={labelOf(wid)}
                        onRemove={() => removeWidgetFromRow(row.id, wid)}
                        data-testid={`row-widget-chip-${row.id}-${wid}`}
                        removeTestId={`row-widget-chip-remove-${row.id}-${wid}`}
                      />
                    ))
                  )}
                </div>
              </div>
              <button
                type="button"
                className="icon-btn-delete"
                onClick={() => removeRow(row.id)}
                aria-label="Slett rad"
                data-testid={`row-delete-${row.id}`}
              >
                <Trash2 size={16} />
              </button>
            </DropZone>
          ))}
          <button
            type="button"
            className="btn ghost icon-btn"
            onClick={addRow}
            data-testid="add-row-button"
          >
            <Plus size={14} />
            Legg til rad
          </button>
        </div>
      </DndContext>
    </div>
  );
}

function DropZone({
  id,
  className,
  children,
  "data-testid": testId,
}: {
  id: string;
  className: string;
  children: ReactNode;
  "data-testid": string;
}) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={`${className}${isOver ? " over" : ""}`}
      data-testid={testId}
    >
      {children}
    </div>
  );
}

function Chip({
  from,
  widgetRef,
  label,
  onRemove,
  "data-testid": testId,
  removeTestId,
}: {
  from: string;
  widgetRef: string;
  label: string;
  onRemove?: () => void;
  "data-testid": string;
  removeTestId?: string;
}) {
  // The draggable id names the container too: a leftover duplicate puts the
  // same reference on two rows, and the ids must still be unique.
  const source: ChipSource = { from, ref: widgetRef };
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({ id: `${from}|${widgetRef}`, data: source });
  const style: CSSProperties = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
  };
  return (
    <span
      ref={setNodeRef}
      style={style}
      className={`widget-pill${isDragging ? " dragging" : ""}`}
      data-testid={testId}
    >
      <span
        className="widget-pill-label widget-pill-grab"
        data-testid={`${testId}-handle`}
        {...attributes}
        {...listeners}
      >
        {label}
      </span>
      {onRemove ? (
        <button
          type="button"
          className="widget-pill-remove"
          onClick={onRemove}
          aria-label="Fjern"
          data-testid={removeTestId}
        >
          <X size={12} />
        </button>
      ) : null}
    </span>
  );
}
